import type { StateCreator } from 'zustand';
import { produceMutation } from '@epihub/web/src/zustand/produceMutation';
import {
  DEFAULT_VISITOR_NAME,
  getDefaultQuickResponses,
  type SupportedLanguage,
} from '@krishanmadan/core/src/model/const';
import { type QuickResponse } from '@krishanmadan/core/src/types';
import { languageDefault } from '../lib/locale';

export type SettingsStoreSlice = {
  settings: {
    visitorId: string;
    __setVisitorId: (visitorId: string) => void;

    visitorName: string;
    setVisitorName: (name: string) => void;

    misuseRate: number;
    __setMisuseRate: (misuseRate: number) => void;
    accumulateMisuseRate: (misuseRate: number) => void;

    language: SupportedLanguage;
    updateLanguage: (language: SupportedLanguage) => void;

    soundOn: boolean;
    updateSoundOn: (soundOn: boolean) => void;

    calendarDrawerOpen: boolean;
    setCalendarDrawerOpen: (open: boolean) => void;

    quickResponses: QuickResponse[];
    setQuickResponses: (qr: QuickResponse[]) => void;
  };
};

export const createSettingsStoreSlice: StateCreator<SettingsStoreSlice> = (set, getState) => ({
  settings: {
    // This is a hack to allow ts to be happy when using visitorId from the store
    // In reality we need to fetch this async meaning that
    // we rely on the App.tsx to ensure nothing gets rendered before having the visitorId
    // see useInitializeVisitor
    visitorId: null as unknown as string,
    __setVisitorId: produceMutation(set, (state, visitorId: string) => {
      state.settings.visitorId = visitorId;
    }),

    visitorName: DEFAULT_VISITOR_NAME,
    setVisitorName: produceMutation(set, (state, name: string) => {
      state.settings.visitorName = name;
    }),

    language: languageDefault,
    updateLanguage: produceMutation(set, (state, language: SupportedLanguage) => {
      state.settings.language = language;
    }),

    soundOn: true,
    updateSoundOn: produceMutation(set, (state, soundOn: boolean) => {
      state.settings.soundOn = soundOn;
    }),

    calendarDrawerOpen: false,
    setCalendarDrawerOpen: produceMutation(set, (state, calendarDrawerOpen: boolean) => {
      state.settings.calendarDrawerOpen = calendarDrawerOpen;
    }),

    quickResponses: getDefaultQuickResponses(languageDefault),
    setQuickResponses: produceMutation(set, (state, qr: QuickResponse[]) => {
      state.settings.quickResponses = qr;
    }),

    misuseRate: 0,
    __setMisuseRate: produceMutation(set, (state, misuseRate: number) => {
      state.settings.misuseRate = misuseRate;
    }),
    accumulateMisuseRate: produceMutation(set, (state, misuseRate: number) => {
      // Only values > 0.5 are considered, that means that if we set the limit
      // at 3 the user needs to send (3/0.6=5) messages before being blocked
      if (misuseRate > 0.5) {
        state.settings.misuseRate = state.settings.misuseRate + misuseRate;
      }
    }),
  },
});
